import { loadFacebookSdk } from '../utils/facebookSdk';

// Registro integrado de WhatsApp (Embedded Signup) de Meta. El cliente autoriza en una ventana de Meta y elige o crea su cuenta de
// WhatsApp Business y su número; aquí solo se juntan las dos respuestas que llegan por separado (el código del inicio de sesión y,
// por mensaje de la ventana, los identificadores de la cuenta y del número) para mandarlas al motor, que las verifica con Meta.
const APP_ID = process.env.REACT_APP_META_APP_ID || '';
const CONFIG_ID = process.env.REACT_APP_META_CONFIG_ID || '';
const WAIT_MS = 5 * 60 * 1000;
const IDS_AFTER_CODE_MS = 15 * 1000;

export const signupConfigured = Boolean(APP_ID && CONFIG_ID);
export const missingSignupVars = [!APP_ID && 'REACT_APP_META_APP_ID', !CONFIG_ID && 'REACT_APP_META_CONFIG_ID'].filter(Boolean);

export class SignupCancelled extends Error {}

/** Carga el SDK con anticipación: la ventana de Meta debe abrirse enseguida después del clic o el navegador la bloquea. */
export function preloadSignup() { if (signupConfigured) loadFacebookSdk(APP_ID).catch(() => {}); }

/** Interpreta un mensaje de la ventana de Meta. Solo se aceptan los que vienen de facebook.com. Devuelve null si no es del registro. */
export function parseSignupMessage(origin, raw) {
  if (!/^https:\/\/(www|web)\.facebook\.com$/.test(origin)) return null;
  let d = raw;
  if (typeof raw === 'string') { try { d = JSON.parse(raw); } catch { return null; } }
  if (!d || d.type !== 'WA_EMBEDDED_SIGNUP') return null;
  if (d.event === 'FINISH') {
    const { waba_id: wabaId, phone_number_id: phoneNumberId } = d.data || {};
    if (!/^\d{5,25}$/.test(String(wabaId || '')) || !/^\d{5,25}$/.test(String(phoneNumberId || ''))) return { kind: 'error', message: 'Meta no devolvió la cuenta o el número. Vuelve a intentarlo.' };
    return { kind: 'finish', wabaId: String(wabaId), phoneNumberId: String(phoneNumberId) };
  }
  if (d.event === 'FINISH_ONLY_WABA') return { kind: 'error', message: 'Se creó o eligió la cuenta, pero no se agregó ningún número. Vuelve a empezar y agrega un número.' };
  if (d.event === 'CANCEL') return { kind: 'cancel' };
  return null;
}

/** Abre la ventana de Meta y devuelve {code, wabaId, phoneNumberId}. El código vence en pocos minutos: úsalo enseguida. */
export function startEmbeddedSignup() {
  return new Promise((resolve, reject) => {
    if (!signupConfigured) { reject(new Error(`Falta configurar ${missingSignupVars.join(' y ')} en el .env del admin.`)); return; }
    let code = null; let ids = null; let done = false; let idsTimer = null;
    const cleanup = () => { done = true; window.removeEventListener('message', onMessage); clearTimeout(idsTimer); clearTimeout(overall); };
    const finish = (fn, v) => { if (done) return; cleanup(); fn(v); };
    const check = () => {
      if (code && ids) finish(resolve, { code, ...ids });
      else if (code && !idsTimer) idsTimer = setTimeout(() => finish(reject, new Error('Meta no avisó qué número se conectó. Vuelve a intentarlo.')), IDS_AFTER_CODE_MS);
    };
    function onMessage(e) {
      const m = parseSignupMessage(e.origin, e.data);
      if (!m) return;
      if (m.kind === 'finish') { ids = { wabaId: m.wabaId, phoneNumberId: m.phoneNumberId }; check(); }
      else if (m.kind === 'cancel') finish(reject, new SignupCancelled('Cancelaste el registro en la ventana de Meta.'));
      else finish(reject, new Error(m.message));
    }
    const overall = setTimeout(() => finish(reject, new Error('Pasó mucho tiempo sin terminar el registro. Vuelve a intentarlo.')), WAIT_MS);
    window.addEventListener('message', onMessage);
    loadFacebookSdk(APP_ID).then((FB) => {
      FB.login((response) => {
        const c = response?.authResponse?.code;
        if (!c) { finish(reject, new SignupCancelled('No se completó el inicio de sesión con Meta.')); return; }
        code = c; check();
      }, { config_id: CONFIG_ID, response_type: 'code', override_default_response_type: true, extras: { setup: {}, featureType: '', sessionInfoVersion: '3' } });
    }).catch((err) => finish(reject, err));
  });
}
